import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FuelAnomaly,
  FuelSummary,
  getFuelSummary,
  listFuelAnomalies,
  parseTokenClaims,
  triggerFuelScan,
} from "../../api";
import { Icon } from "../../icons";

export function FuelPage() {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const isAdmin = claims?.role === "ADMIN" || claims?.role === "OPERATOR";

  const [summary, setSummary] = useState<FuelSummary | null>(null);
  const [anomalies, setAnomalies] = useState<FuelAnomaly[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const [s, a] = await Promise.all([
        getFuelSummary().catch((e) => {
          throw e;
        }),
        listFuelAnomalies(),
      ]);
      setSummary(s);
      setAnomalies(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onScan() {
    setScanning(true);
    setScanInfo(null);
    setError(null);
    try {
      const r = await triggerFuelScan();
      setScanInfo(
        t("fuel.scanResult", { samples: r.samples_scanned, found: r.anomalies_found }),
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setScanning(false);
    }
  }

  if (loading) {
    return <p className="muted">{t("common:actions.loading")}</p>;
  }

  const hasSummary = summary && summary.vehicles.length > 0;
  const hasAnomalies = anomalies && anomalies.length > 0;

  return (
    <div className="page">
      <div className="page-head-row">
        <div>
          <h2 style={{ marginTop: 0 }}>
            <Icon name="fuel" /> {t("fuel.title")}
          </h2>
          <p className="muted">{t("fuel.intro")}</p>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="ghost"
            onClick={onScan}
            disabled={scanning}
          >
            {scanning ? t("fuel.scanning") : t("fuel.scanNow")}
          </button>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {scanInfo && <p className="info">{scanInfo}</p>}

      <h3 style={{ marginTop: "1.5rem" }}>{t("fuel.summaryTitle")}</h3>
      {!hasSummary ? (
        <div className="empty-card">
          <Icon name="fuel" /> {t("fuel.emptyTelemetry")}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("fuel.th.vehicle")}</th>
              <th>{t("fuel.th.firm")}</th>
              <th>{t("fuel.th.samples")}</th>
              <th>{t("fuel.th.avgLph")}</th>
              <th>{t("fuel.th.min")}</th>
              <th>{t("fuel.th.max")}</th>
            </tr>
          </thead>
          <tbody>
            {summary!.vehicles.map((v) => (
              <tr key={v.vehicle_id}>
                <td>{v.vehicle_id}</td>
                <td>{v.firm_id}</td>
                <td>{v.sample_count}</td>
                <td>{v.avg_lph.toFixed(2)}</td>
                <td>{v.min_lph.toFixed(2)}</td>
                <td>{v.max_lph.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={{ marginTop: "1.5rem" }}>{t("fuel.anomaliesTitle")}</h3>
      {!hasAnomalies ? (
        <div className="empty-card">
          <Icon name="check" /> {t("fuel.noAnomalies")}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("fuel.anomalyTh.time")}</th>
              <th>{t("fuel.anomalyTh.vehicle")}</th>
              <th>{t("fuel.anomalyTh.severity")}</th>
              <th>{t("fuel.anomalyTh.observed")}</th>
              <th>{t("fuel.anomalyTh.baseline")}</th>
              <th>{t("fuel.anomalyTh.zscore")}</th>
              <th>{t("fuel.anomalyTh.alert")}</th>
            </tr>
          </thead>
          <tbody>
            {anomalies!.map((a) => (
              <tr key={a.id} className={`severity-${a.severity}`}>
                <td>{new Date(a.detected_at).toLocaleString(i18n.resolvedLanguage)}</td>
                <td>{a.vehicle_id}</td>
                <td>
                  <span className={`severity-pill sev-${a.severity}`}>
                    {a.severity}
                  </span>
                </td>
                <td>{a.observed_lph.toFixed(2)}</td>
                <td>{a.baseline_lph.toFixed(2)}</td>
                <td>{a.z_score.toFixed(2)}</td>
                <td>
                  {a.alert_event_id ? (
                    <code className="small">{a.alert_event_id}</code>
                  ) : (
                    <span className="muted small">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
